import { Injectable, inject } from '@angular/core';
import { Listing } from '@df/catalog-ui/listing/listing';
import { ISortOptions } from '@df/catalog-ui/listing/sort/abstract-async.engine';
import { IListingSortOption } from '@df/catalog-ui/listing/sort/listing-sort';
import { Product } from '@df/catalog/product/product';
import { Logger } from '@df/core/service/logger';
import { ListingSortKlevuEngine } from '@df/module-klevu-search/listing-sort-klevu.engine';
import intersection from 'lodash-es/intersection';

@Injectable({
    providedIn: 'root'
})
export class ListingSortKlevuEngineDecorator extends ListingSortKlevuEngine {
    private logger = inject(Logger);

    public override sort(collection: Product[], option: IListingSortOption, listing: Listing<Product>, cb: (models: Product[]) => void): void {
        const heroProducts = listing.config?.source?.data?.heroProducts || [];

        if (heroProducts.length) {
            // push hero products to the bottom before setting up their final position,
            // its needed because otherwise we can remove products form top after setting other hero position and breaking the order this way
            collection = collection?.sort((a, b) => {
                if (heroProducts.find(i => i.product_id === a.id)) return 1;
                if (heroProducts.find(i => i.product_id === b.id)) return -1;
                return 0;
            });
        }

        // Apply hero product positioning with validation
        const validHeroProducts = heroProducts.filter(heroProduct =>
            this.validateHeroProductPosition(heroProduct, collection.length)
        );

        if (validHeroProducts.length !== heroProducts.length) {
            this.logger.warn('Some hero products were filtered out due to invalid position data', {
                totalHeroProducts: heroProducts.length,
                validHeroProducts: validHeroProducts.length,
                categoryId: listing.config?.source?.id
            });
        }

        validHeroProducts.forEach(heroProduct => {
            try {
                const matchingProduct = collection.find(product => product.id === heroProduct.product_id);

                if (matchingProduct) {
                    const insertIndex = heroProduct.position - 1;
                    const currentIndex = collection.indexOf(matchingProduct);

                    if (currentIndex >= 0) {
                        collection.splice(currentIndex, 1);
                        collection.splice(insertIndex, 0, matchingProduct);

                        this.logger.debug('Hero product positioned successfully', {
                            productId: heroProduct.product_id,
                            position: heroProduct.position,
                            insertIndex
                        });
                    }
                }
            } catch (error) {
                this.logger.error('Error positioning hero product', {
                    heroProduct,
                    error: error instanceof Error ? error.message : String(error),
                    categoryId: listing.config?.source?.id
                });
            }
        });
        super.sort(collection, option, listing, cb);
    }

    protected override getSortOptions(listing: Listing<Product>): ISortOptions {
        const sortOptions = super.getSortOptions(listing);

        if (listing.config.source.data.heroProducts?.length) {
            sortOptions.bannerPositionsUnchanged = false;
        }

        return sortOptions;
    }

    protected override runCallback(listing: Listing<Product>, cb: (err: Error | string | null, ids: number[]) => void, ids?: number[]) {
        const heroProducts = listing.config.source.data.heroProducts || [];

        if (!ids?.length && listing?.config?.source?.data?.products?.length) {
            ids = listing.config.source.data.products;
        } else if (ids?.length && listing?.config?.source?.data?.products?.length) {
            ids = intersection(ids, listing?.config?.source?.data?.products);
        }

        if (heroProducts.length) {
            // push hero products to the bottom before setting up their final position,
            // its needed because otherwise we can remove products form top after setting other hero position and breaking the order this way
            ids = ids?.sort((a, b) => {
                if (heroProducts.find(i => i.product_id === a)) return 1;
                if (heroProducts.find(i => i.product_id === b)) return -1;
                return 0;
            });
        }

        // Apply hero product positioning with validation
        const validHeroProducts = heroProducts.filter(heroProduct =>
            this.validateHeroProductPosition(heroProduct, ids?.length || 0)
        );

        if (validHeroProducts.length !== heroProducts.length) {
            this.logger.warn('Some hero products were filtered out due to invalid position data', {
                totalHeroProducts: heroProducts.length,
                validHeroProducts: validHeroProducts.length,
                categoryId: listing.config?.source?.id
            });
        }

        validHeroProducts.forEach(heroProduct => {
            try {
                const matchingProduct = ids?.find(id => id === heroProduct.product_id);
                if (matchingProduct) {
                    const insertIndex = heroProduct.position - 1;
                    const currentIndex = ids?.indexOf(matchingProduct);

                    if (currentIndex !== undefined && currentIndex >= 0) {
                        ids?.splice(currentIndex, 1);
                        ids?.splice(insertIndex, 0, matchingProduct);

                        this.logger.debug('Hero product positioned successfully', {
                            productId: heroProduct.product_id,
                            position: heroProduct.position,
                            insertIndex
                        });
                    }
                } else {
                    // Product not in current results, insert it at the specified position
                    ids?.splice(heroProduct.position - 1, 0, heroProduct.product_id);

                    this.logger.debug('Hero product inserted at position', {
                        productId: heroProduct.product_id,
                        position: heroProduct.position
                    });
                }
            } catch (error) {
                this.logger.error('Error positioning hero product', {
                    heroProduct,
                    error: error instanceof Error ? error.message : String(error),
                    categoryId: listing.config?.source?.id
                });
            }
        });

        super.runCallback(listing, cb, ids);
    }

    /**
     * Validates hero product position data
     * @param heroProduct The hero product to validate
     * @param maxPosition Maximum valid position (collection length)
     * @returns true if valid, false otherwise
     */
    private validateHeroProductPosition(heroProduct: {
        product_id: number;
        position: number;
    }, maxPosition: number): boolean {
        if (!heroProduct) {
            this.logger.warn('Hero product is null or undefined', { heroProduct });
            return false;
        }

        if (!heroProduct.product_id) {
            this.logger.warn('Hero product missing product_id', { heroProduct });
            return false;
        }

        if (typeof heroProduct.position !== 'number' || heroProduct.position < 1) {
            this.logger.warn('Hero product has invalid position (must be positive number)', {
                heroProduct,
                position: heroProduct.position
            });
            return false;
        }

        if (heroProduct.position > maxPosition) {
            this.logger.warn('Hero product position exceeds maximum allowed position', {
                heroProduct,
                position: heroProduct.position,
                maxPosition
            });
            return false;
        }

        return true;
    }
}
